import React, { useEffect, useCallback } from 'react';
import { bool, func, number, object, objectOf, oneOfType, shape, string } from 'prop-types';
import NumberFormat from 'react-number-format';

import Min from '@assets/Icon/svg/Min';
import Plus from '@assets/Icon/svg/Plus';
import { noop } from '@ajaibid/utils';
import { R500 } from '@assets/colors';
import { thousandSeparator, decimalToInt, decimalFormat } from '@ajaibid/utils';
import { Typography } from '@ajaibid/components';
import { Button } from 'Component/Ajaib';
import { stySpinner } from './_style';
import useField from './hooks/useField';

const FormSpinner = (props) => {
  const {
    className,
    defaultValue,
    decimalScale,
    decimalSeparator,
    disabled,
    error,
    id,
    maxValue,
    name,
    numStep,
    onChange,
    onError,
    onValid,
    placeholder,
    validators,
    style,
    numThousandSeparator,
  } = props;
  const [fieldValue, { updateField, updateForm, error: errorField, errorKey }] = useField(name, {
    defaultValue,
    validators,
  });

  const onErrorCb = useCallback((params) => onError(params), [onError]);
  const onValidCb = useCallback((params) => onValid(params), [onValid]);
  const intValue =
    decimalScale > 0 ? parseFloat(decimalToInt(fieldValue)) : decimalToInt(fieldValue);

  const handleChange = (event) => {
    const newValue = parseFloat(event.value, 10);
    if (onChange) {
      onChange(event, { updateField, updateForm });
    } else if (newValue > maxValue && maxValue !== -1) {
      updateField(maxValue);
    } else {
      updateField(event.value);
    }
  };

  const handleInc = () => {
    const newValue = intValue + numStep;
    const realValue =
      decimalScale > 0 ? decimalFormat(newValue, decimalScale) : thousandSeparator(newValue);
    if (newValue <= maxValue || maxValue === -1) {
      updateField(realValue);
    } else {
      updateField(thousandSeparator(maxValue));
    }
  };

  const handleDec = () => {
    const newValue = intValue - numStep;
    const realValue =
      decimalScale > 0 ? decimalFormat(newValue, decimalScale) : thousandSeparator(newValue);
    if (newValue > 0) updateField(realValue);
  };

  useEffect(() => {
    if (errorField) {
      onErrorCb({ errorMessage: errorField, errorKey });
    } else {
      onValidCb();
    }
  }, [errorField, errorKey, onErrorCb, onValidCb]);

  // TODO: this line breaks validator. Wait for QA regression, if it works fine without it, remove
  // useEffect(() => {
  //   if (propsError) {
  //     updateForm(name, fieldValue, propsError);
  //   }
  // }, [propsError, name, updateForm]);

  const styProps = {
    error: error || errorField,
  };

  const errorText = error || errorField;

  return (
    <div style={{ width: '100%' }}>
      <div className={stySpinner(styProps)}>
        <Button
          variant="icon"
          onClick={handleDec}
          disabled={disabled}
          data-testid={`btnMin-${name}`}
        >
          <Min />
        </Button>
        <NumberFormat
          id={id}
          name={name}
          className={className}
          thousandSeparator={numThousandSeparator}
          decimalSeparator={decimalSeparator}
          value={fieldValue}
          placeholder={placeholder}
          allowNegative={false}
          onValueChange={handleChange}
          decimalScale={decimalScale}
          isAllowed={({ floatValue, formattedValue }) =>
            formattedValue === '' ||
            floatValue <= maxValue ||
            maxValue === -1 ||
            formattedValue[0] === '0'
          }
          disabled={disabled}
          data-testid={`input-${name}`}
          style={style}
        />
        <Button
          variant="icon"
          onClick={handleInc}
          disabled={disabled}
          data-testid={`btnPlus-${name}`}
        >
          <Plus />
        </Button>
      </div>
      {errorText ? (
        <div>
          <Typography
            type="label"
            color={R500}
            style={{ marginTop: 0 }}
            align="right"
            data-testid={`divError-${name}`}
          >
            {errorText}
          </Typography>
        </div>
      ) : null}
    </div>
  );
};

FormSpinner.propTypes = {
  id: string.isRequired,
  name: string.isRequired,
  className: string,
  decimalScale: number,
  decimalSeparator: string,
  defaultValue: oneOfType([string, number]),
  disabled: bool,
  error: string,
  maxValue: number,
  numStep: number,
  numThousandSeparator: oneOfType([string, bool]),
  onChange: oneOfType([func, bool]),
  onError: func,
  onValid: func,
  placeholder: string,
  style: object,
  validators: objectOf(
    shape({
      validator: func,
      message: string,
    })
  ),
};

FormSpinner.defaultProps = {
  className: '',
  decimalScale: 0,
  decimalSeparator: ',',
  defaultValue: 0,
  disabled: false,
  error: '',
  maxValue: -1,
  numStep: 1,
  numThousandSeparator: '.',
  onChange: false,
  onError: noop,
  onValid: noop,
  placeholder: '',
  style: {},
  validators: {},
};

export default FormSpinner;
